import React, { useEffect, useState } from "react";
import {
    ArrowLeft, ArrowRight, Check, Copy, ExternalLink, Lightbulb,
    AlertTriangle, SkipForward, Zap, ChevronDown, Loader2, Rocket,
} from "lucide-react";
import { api } from "@/lib/api";
import {
    getGroupKeywords, getGroupNegatives,
    collectGroupHeadlines, collectGroupDescriptions,
} from "@/lib/adAssets";

/* ─────────────────────────────────────────────────────────────────────────
   LaunchWizard — 11 steps matching the real Google Ads (2026) UI flow.

   Works only for Search campaigns; other types show a stub.

   localStorage shape:
   {
       currentStep: 1..11,
       currentCampaignIdx: number,
       currentGroupIdx: number,
       completed: { "<key>": true, ... },
   }
   ───────────────────────────────────────────────────────────────────────── */

const WIZARD_KEY = (uid) => `autoads_wizard_${uid}`;
const BASE_STEPS = 10;

// Steps tied to a specific ad group:
//   - step 8: always the FIRST group (group index 0) — during campaign-creation wizard
//     (extensions are also included on this step so user copies everything for the
//     first group at once)
//   - steps 11, 12, ...: additional groups (index 1, 2, ...) added AFTER the campaign is published
function groupIndexForStep(step) {
    if (step === 8) return 0;
    if (step >= 11) return step - 10; // step 11 → group 1, step 12 → group 2, ...
    return null;
}

export default function LaunchWizard({ userId, strategy, bizInfo, onSkip, onFinish, onProgressChange, initialCampaignIdx = null }) {
    const campaigns = strategy?.campaigns || (strategy?.campaign ? [strategy.campaign] : []);
    const searchCampaigns = campaigns.filter(c => (c.type || "Search").toLowerCase() === "search");
    const nonSearchCampaigns = campaigns.filter(c => (c.type || "Search").toLowerCase() !== "search");

    const [state, setState] = useState({
        currentStep: 1,
        currentCampaignIdx: 0,
        completed: {},
        edits: {}, // user-edited overrides (e.g. { firstGroupName: "My Group" })
    });
    const [loaded, setLoaded] = useState(false);
    // Server-side launched state — drives the "✓ Launched" badges on campaign tabs.
    const [launchedIndexes, setLaunchedIndexes] = useState([]);
    const [launching, setLaunching] = useState(false);
    const [launchError, setLaunchError] = useState("");

    useEffect(() => {
        if (!userId) return;
        try {
            const saved = localStorage.getItem(WIZARD_KEY(userId));
            if (saved) {
                const parsed = JSON.parse(saved);
                // Migrate older shape that had currentGroupIdx
                delete parsed.currentGroupIdx;
                setState(parsed);
            }
        } catch {}
        // Load already-launched indexes from API
        // PR #8: після завантаження — авто-вибір правильної кампанії:
        //   1) Якщо в URL був ?campaign=N → ставимо її (deep-link з Health Check)
        //   2) Інакше — першу НЕзапущену (бо запущену юзер вже бачив)
        //   3) Якщо всі запущені — залишаємо як є (idx=0, для перегляду)
        (async () => {
            try {
                const r = await api.get("/strategy");
                const li = r.data?.launched_indexes || (r.data?.launched ? [0] : []);
                setLaunchedIndexes(li);

                setState((s) => {
                    // Якщо юзер вже почав щось у Wizard'і (currentStep > 1 або completed має ключі),
                    // не перебиваємо його прогрес — поважаємо localStorage.
                    const hasProgress = s.currentStep > 1 || Object.keys(s.completed || {}).length > 0;
                    if (hasProgress) return s;

                    // 1) Deep-link з URL
                    if (initialCampaignIdx != null && initialCampaignIdx < searchCampaigns.length) {
                        return { ...s, currentCampaignIdx: initialCampaignIdx };
                    }
                    // 2) Перша незапущена
                    const launchedSet = new Set(li);
                    const firstUnlaunched = searchCampaigns.findIndex((_, i) => !launchedSet.has(i));
                    if (firstUnlaunched >= 0 && firstUnlaunched !== s.currentCampaignIdx) {
                        return { ...s, currentCampaignIdx: firstUnlaunched };
                    }
                    // 3) Все запущено — залишаємо idx=0
                    return s;
                });
            } catch {}
        })();
        setLoaded(true);
    }, [userId]);

    useEffect(() => {
        if (!loaded || !userId) return;
        try { localStorage.setItem(WIZARD_KEY(userId), JSON.stringify(state)); } catch {}
        onProgressChange?.(state);
    }, [state, loaded, userId, onProgressChange]);

    if (!searchCampaigns.length && nonSearchCampaigns.length) {
        return <NonSearchStub campaigns={nonSearchCampaigns} onSkip={onSkip} />;
    }
    if (!searchCampaigns.length) {
        return (
            <div className="mt-6 glass rounded-2xl p-7 text-center" data-testid="wizard-no-campaigns">
                <p className="text-secondary-c">Не знайдено жодної кампанії у стратегії.</p>
            </div>
        );
    }

    const campaign = searchCampaigns[state.currentCampaignIdx] || searchCampaigns[0];
    const adGroups = campaign?.ad_groups || [];
    const totalGroups = adGroups.length;
    const totalSteps = BASE_STEPS + Math.max(0, totalGroups - 1);

    const groupIdxForCurrent = groupIndexForStep(state.currentStep);
    const group = groupIdxForCurrent != null ? adGroups[groupIdxForCurrent] : null;

    const stepKey = (n) => {
        const gi = groupIndexForStep(n);
        return gi != null ? `c${state.currentCampaignIdx}-s${n}-g${gi}` : `c${state.currentCampaignIdx}-s${n}`;
    };

    const markDoneAndNext = (nextStep) => {
        setState(s => ({
            ...s,
            completed: { ...s.completed, [stepKey(s.currentStep)]: true },
            currentStep: nextStep ?? Math.min(totalSteps, s.currentStep + 1),
        }));
        if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const goBack = () => {
        setState(s => ({ ...s, currentStep: Math.max(1, s.currentStep - 1) }));
        if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
    };

    // Apply user edits (e.g. user-renamed first ad group) so all downstream
    // step content and group badges reflect the edited name.
    const effectiveGroup = group && groupIdxForCurrent === 0 && state.edits?.firstGroupName
        ? { ...group, name: state.edits.firstGroupName }
        : group;

    const stepContent = buildStep({
        step: state.currentStep,
        totalSteps,
        campaign,
        group: effectiveGroup,
        groupIdx: groupIdxForCurrent ?? 0,
        totalGroups,
        strategy,
        bizInfo,
    });

    const setEdit = (key, value) => {
        setState(s => ({ ...s, edits: { ...(s.edits || {}), [key]: value } }));
    };

    const handleFinish = async () => {
        const key = stepKey(state.currentStep);
        setState(s => ({ ...s, completed: { ...s.completed, [key]: true } }));

        const currentIdx = state.currentCampaignIdx;
        // Persist this campaign as launched on the server.
        setLaunchError("");
        setLaunching(true);
        let updatedLaunched = launchedIndexes;
        try {
            const r = await api.post("/campaign/launch", { campaign_idx: currentIdx });
            updatedLaunched = r.data?.launched_indexes || [...launchedIndexes, currentIdx];
            setLaunchedIndexes(updatedLaunched);
        } catch (e) {
            setLaunchError(e?.response?.data?.detail || "Не вдалося позначити кампанію як запущену");
            setLaunching(false);
            return;
        }
        setLaunching(false);

        // If there are more un-launched Search campaigns, jump to the next one
        // instead of ending the wizard. Otherwise, defer to the parent.
        const nextUnlaunchedIdx = searchCampaigns.findIndex((_, i) => !updatedLaunched.includes(i));
        if (nextUnlaunchedIdx >= 0 && nextUnlaunchedIdx !== currentIdx) {
            setState(s => ({
                ...s,
                currentCampaignIdx: nextUnlaunchedIdx,
                currentStep: 1,
            }));
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }
        // All campaigns done — propagate to parent.
        onFinish?.();
    };

    const isFinalStep = state.currentStep === totalSteps;

    return (
        <div className="mt-6" data-testid="launch-wizard">
            <WizardHeader
                step={state.currentStep}
                totalSteps={totalSteps}
                campaigns={searchCampaigns}
                currentCampaignIdx={state.currentCampaignIdx}
                launchedIndexes={launchedIndexes}
                onChangeCampaign={(idx) => setState(s => ({ ...s, currentCampaignIdx: idx, currentStep: 1 }))}
                onSkip={onSkip}
            />

            {launchError && (
                <div className="mt-3 p-3 rounded-lg border border-error/30 bg-error/8 text-sm text-error-c font-mono" data-testid="wizard-launch-error">
                    {launchError}
                </div>
            )}

            {nonSearchCampaigns.length > 0 && state.currentStep === 1 && (
                <div className="mt-4 flex items-start gap-3 p-3 rounded-lg" style={{ background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.2)" }}>
                    <AlertTriangle size={14} className="text-warning-c mt-0.5 shrink-0" />
                    <p className="text-xs text-text-primary/85">
                        У стратегії є {nonSearchCampaigns.length} не-Search {nonSearchCampaigns.length === 1 ? "кампанія" : "кампаній"} (PerformanceMax / Display / Shopping). Wizard підтримує лише Search — для інших типів використовуйте дані з табу «Стратегія».
                    </p>
                </div>
            )}

            <WizardStep
                key={`step-${state.currentStep}`}
                step={state.currentStep}
                totalSteps={totalSteps}
                content={stepContent}
                groupIdx={groupIdxForCurrent}
                totalGroups={totalGroups}
                isStepDone={!!state.completed[stepKey(state.currentStep)]}
                edits={state.edits || {}}
                onEditChange={setEdit}
                isFinalStep={isFinalStep}
                launching={launching}
                hasMoreCampaigns={isFinalStep && searchCampaigns.length > 1 && searchCampaigns.some((_, i) => i !== state.currentCampaignIdx && !launchedIndexes.includes(i))}
                onBack={state.currentStep > 1 ? goBack : null}
                onDone={() => {
                    if (isFinalStep) { handleFinish(); return; }
                    markDoneAndNext();
                }}
            />
        </div>
    );
}

/* ─────────────────────────────── Header ───────────────────────────────── */
const WizardHeader = ({ step, totalSteps, campaigns, currentCampaignIdx, launchedIndexes = [], onChangeCampaign, onSkip }) => {
    const pct = Math.round((step / totalSteps) * 100);
    const launchedSet = new Set(launchedIndexes);
    const totalLaunched = launchedIndexes.filter(i => i < campaigns.length).length;
    const multi = campaigns.length > 1;

    return (
        <section className="glass rounded-2xl p-6" data-testid="wizard-header">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3 min-w-0">
                    <Zap size={20} className="text-primary-c shrink-0" />
                    <div className="min-w-0">
                        <div className="font-display font-bold text-lg text-text-primary truncate">
                            Крок <span className="text-primary-c">{step}</span> з {totalSteps}
                        </div>
                        {multi ? (
                            <div className="mt-1 text-xs font-mono text-secondary-c">
                                Кампанія <span className="text-text-primary">{currentCampaignIdx + 1}</span> з <span className="text-text-primary">{campaigns.length}</span>
                                {" · "}
                                запущено <span className="text-success-c">{totalLaunched}</span>/{campaigns.length}
                            </div>
                        ) : (
                            <div className="mt-1 text-xs font-mono text-secondary-c truncate">
                                {campaigns[currentCampaignIdx]?.name}
                            </div>
                        )}
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <a
                        href="https://ads.google.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-primary/30 bg-primary/10 text-primary-c hover:bg-primary/20 transition text-xs font-medium"
                        data-testid="wizard-open-ads"
                    >
                        <ExternalLink size={12} /> Відкрити Google Ads
                    </a>
                    <button
                        onClick={onSkip}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs text-secondary-c hover:text-text-primary transition"
                        data-testid="wizard-skip-btn"
                    >
                        Пропустити wizard <SkipForward size={12} />
                    </button>
                </div>
            </div>

            {/* Campaign pills — only when multi-campaign */}
            {multi && (
                <div className="mt-4 flex flex-wrap gap-2" data-testid="wizard-campaign-pills">
                    {campaigns.map((c, i) => {
                        const isLaunched = launchedSet.has(i);
                        const isCurrent = i === currentCampaignIdx;
                        return (
                            <button
                                key={i}
                                onClick={() => onChangeCampaign(i)}
                                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition border ${
                                    isCurrent
                                        ? "border-primary bg-primary/15 text-primary-c"
                                        : isLaunched
                                          ? "border-success/40 bg-success/8 text-success-c hover:bg-success/15"
                                          : "border-border text-secondary-c hover:border-primary/40 hover:text-text-primary"
                                }`}
                                data-testid={`wizard-campaign-pill-${i}`}
                            >
                                <span className="font-mono text-[10px] opacity-70">#{i + 1}</span>
                                <span className="max-w-[160px] truncate">{c.name}</span>
                                {isLaunched && <Check size={12} className="shrink-0" />}
                            </button>
                        );
                    })}
                </div>
            )}

            <div className="mt-5 h-2 bg-surface rounded-full overflow-hidden border border-border">
                <div
                    className="h-full transition-[width] duration-500 ease-out"
                    style={{
                        width: `${pct}%`,
                        background: "linear-gradient(90deg, #6D5EF8 0%, #2DD4BF 100%)",
                    }}
                    data-testid="wizard-progress-bar"
                />
            </div>

            {/* Overall multi-campaign progress meter — separate from current-campaign step */}
            {multi && (
                <div className="mt-3 flex items-center gap-3 text-[11px] font-mono text-faint-c">
                    <span>Загальний прогрес:</span>
                    <div className="flex-1 h-1 bg-surface rounded-full overflow-hidden border border-border">
                        <div
                            className="h-full bg-success-c transition-[width] duration-500"
                            style={{ width: `${(totalLaunched / campaigns.length) * 100}%` }}
                        />
                    </div>
                    <span className="text-secondary-c">{totalLaunched}/{campaigns.length}</span>
                </div>
            )}
        </section>
    );
};

/* ─────────────────────────── Single step body ────────────────────────── */
const WizardStep = ({ step, totalSteps, content, groupIdx, totalGroups, isStepDone, edits, onEditChange, isFinalStep, launching, hasMoreCampaigns, onBack, onDone }) => {
    if (!content) return null;
    // Show group badge on step 8 (first group) AND on additional-group steps (11+).
    const showGroupBadge = totalGroups > 1 && (step === 8 || step >= 11);

    return (
        <section className="mt-6 glass rounded-2xl p-7" data-testid={`wizard-step-${step}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-xs font-mono text-faint-c uppercase tracking-wider">Крок {step} з {totalSteps}</div>
                    <h2 className="mt-1 font-display font-bold text-xl text-text-primary">{content.title}</h2>
                </div>
                {showGroupBadge && (
                    <div className="text-xs font-mono text-secondary-c shrink-0 px-2 py-1 rounded-md border border-border bg-surface-hover/40">
                        Група {groupIdx + 1} / {totalGroups}
                    </div>
                )}
            </div>

            {content.intro && (
                <p className="mt-3 text-sm text-text-primary/85 leading-relaxed whitespace-pre-line">
                    {content.intro}
                </p>
            )}

            {content.fields?.length > 0 && (
                <div className="mt-5 space-y-3" data-testid={`wizard-step-${step}-fields`}>
                    {content.fields.map((f, i) => (
                        <CopyField
                            key={i}
                            field={f}
                            testid={`field-${step}-${i}`}
                            edits={edits}
                            onEditChange={onEditChange}
                        />
                    ))}
                </div>
            )}

            {content.tip && (
                <div className="mt-5 flex items-start gap-3 p-3 rounded-lg" style={{ background: "rgba(109,94,248,0.06)", border: "1px solid rgba(109,94,248,0.18)" }}>
                    <Lightbulb size={14} className="text-primary-c mt-0.5 shrink-0" />
                    <p className="text-xs text-text-primary/90 leading-relaxed whitespace-pre-line">{content.tip}</p>
                </div>
            )}

            {content.warning && (
                <div className="mt-3 flex items-start gap-3 p-3 rounded-lg" style={{ background: "rgba(251,191,36,0.06)", border: "1px solid rgba(251,191,36,0.2)" }}>
                    <AlertTriangle size={14} className="text-warning-c mt-0.5 shrink-0" />
                    <p className="text-xs text-text-primary/90 leading-relaxed">{content.warning}</p>
                </div>
            )}

            <div className="mt-7 pt-6 border-t border-border flex items-center justify-between gap-3 flex-wrap">
                {onBack ? (
                    <button onClick={onBack} className="btn-ghost" disabled={launching} data-testid={`wizard-back-${step}`}>
                        <ArrowLeft size={14} /> Назад
                    </button>
                ) : <div />}
                <button
                    onClick={onDone}
                    disabled={launching}
                    className={`btn-primary ${launching ? "opacity-60 cursor-not-allowed" : "pulse-glow"}`}
                    data-testid={`wizard-done-${step}`}
                >
                    {launching ? (
                        <>Запускаємо… <Loader2 size={14} className="animate-spin" /></>
                    ) : isFinalStep ? (
                        <>
                            {hasMoreCampaigns ? "Запустив — наступна кампанія" : (content.doneLabel || "Я запустив рекламу 🎉")}
                            {hasMoreCampaigns ? <ArrowRight size={14} /> : <Rocket size={14} />}
                        </>
                    ) : (
                        <>{content.doneLabel || "Я зробив це"} <Check size={14} /></>
                    )}
                </button>
            </div>

            {isStepDone && (
                <div className="mt-3 text-right">
                    <span className="text-[11px] font-mono text-success-c inline-flex items-center gap-1">
                        <Check size={11} /> Цей крок вже відмічено як виконаний
                    </span>
                </div>
            )}
        </section>
    );
};

/* ─────────────────────────────── Copy field ───────────────────────────── */
const CopyField = ({ field, testid, edits, onEditChange }) => {
    const [copied, setCopied] = useState(false);
    const isEditable = !!field.editable && !!field.editTarget;
    const editedValue = isEditable && edits ? edits[field.editTarget] : undefined;
    const displayValue = editedValue !== undefined && editedValue !== null
        ? editedValue
        : field.value;
    const empty = !isEditable && (displayValue == null || displayValue === "" || displayValue === "—");

    const copy = async () => {
        if (empty) return;
        try {
            await navigator.clipboard.writeText(String(displayValue));
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {}
    };

    return (
        <div className="rounded-lg border border-border bg-surface-hover/40 p-3" data-testid={testid}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="text-xs font-mono text-faint-c uppercase tracking-wider">{field.label}</div>
                    {isEditable ? (
                        <input
                            type="text"
                            value={displayValue || ""}
                            onChange={(e) => onEditChange?.(field.editTarget, e.target.value)}
                            placeholder={field.value || ""}
                            data-testid={`${testid}-input`}
                            className="mt-1.5 w-full bg-surface border border-border rounded-md px-3 py-2 font-mono text-sm text-text-primary outline-none focus:border-primary/60 transition"
                        />
                    ) : (
                        <div className={`mt-1 font-mono break-words ${empty ? "text-faint-c text-sm" : "text-text-primary text-sm"}`}>
                            {empty ? "—" : (field.multiline ? <pre className="whitespace-pre-wrap text-sm leading-relaxed">{displayValue}</pre> : String(displayValue))}
                        </div>
                    )}
                    {field.hint && (
                        <p className="mt-1.5 text-[11px] text-secondary-c">{field.hint}</p>
                    )}
                </div>
                {!empty && !field.noCopy && (
                    <button
                        onClick={copy}
                        className={`shrink-0 inline-flex items-center gap-1 text-xs font-mono px-2 py-1 rounded-md border transition ${copied ? "border-success text-success-c" : "border-border text-secondary-c hover:border-primary/40 hover:text-text-primary"}`}
                        data-testid={`${testid}-copy`}
                    >
                        {copied ? <Check size={11} /> : <Copy size={11} />}
                        {copied ? "Скопійовано" : "Копіювати"}
                    </button>
                )}
            </div>
        </div>
    );
};

/* ─────────────────────── Non-Search stub ──────────────────────────────── */
const NonSearchStub = ({ campaigns, onSkip }) => (
    <section className="mt-6 glass rounded-2xl p-7" data-testid="wizard-nonsearch-stub">
        <div className="flex items-center gap-3">
            <AlertTriangle size={20} className="text-warning-c" />
            <h2 className="font-display font-bold text-xl text-text-primary">Wizard підтримує лише Search</h2>
        </div>
        <p className="mt-3 text-sm text-secondary-c leading-relaxed">
            У вашій стратегії — кампанія типу <span className="font-mono text-text-primary">{campaigns[0]?.type}</span>. Детальна покрокова інструкція для цього типу скоро з'явиться. А поки що користуйтесь даними з табу <span className="font-mono text-text-primary">«Стратегія»</span>.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
            <a href="https://ads.google.com" target="_blank" rel="noopener noreferrer" className="btn-primary">
                <ExternalLink size={14} /> Відкрити Google Ads
            </a>
            <button onClick={onSkip} className="btn-ghost">
                Перейти до фінального чекліста <ArrowRight size={14} />
            </button>
        </div>
    </section>
);

/* ────────────────────── Step content builder ─────────────────────────── */
function buildStep({ step, totalSteps, campaign, group, groupIdx, totalGroups, strategy, bizInfo }) {
    const budget = strategy?.budget || strategy?.budget_analysis || {};
    const ext = campaign?.extensions || {};
    const dailyBudget = campaign?.daily_budget_usd ?? budget?.daily_usd ?? budget?.daily_budget;
    const conv = strategy?.conversion_tracking || {};

    const businessName = bizInfo?.name;
    const siteUrl = bizInfo?.site_url;
    const businessPhone = bizInfo?.phone;
    const targetAction = (bizInfo?.target_action || [])[0] || conv?.goal_type;

    // Steps 11+ — additional ad groups added AFTER the campaign is published.
    if (step >= 11) {
        return buildAdditionalGroupStep({
            step, totalSteps, group, groupIdx, totalGroups, siteUrl,
        });
    }

    switch (step) {
        // ── 1. Інформація про компанію ─────────────────────────────────
        case 1:
            return {
                title: "Інформація про компанію",
                intro: "У Google Ads натисніть «+ Нова кампанія». На першому екрані Google запитає назву вашого бізнесу та URL сайту — це публічна інформація, яку Google використовує для рекомендацій і відповідних розширень оголошень.",
                fields: [
                    { label: "Назва бізнесу", value: businessName || "—", hint: businessName ? null : "Додайте у Налаштуваннях, якщо хочете автоматично підставляти" },
                    { label: "URL сайту", value: siteUrl || "—", hint: !siteUrl ? "Або URL вашої сторінки в соцмережах / Google Business" : null },
                ],
                tip: "Якщо у вас немає сайту — підійде сторінка в Instagram / Facebook / Google Business. Головне, щоб клієнт міг зв'язатися або забронювати.",
            };

        // ── 2. Ціль кампанії ─────────────────────────────────────────────
        case 2:
            return {
                title: "Ціль кампанії",
                intro: "На екрані «Укажіть ціль своєї кампанії» Google запропонує цілі (Продажі, Ліди, Трафік...). У ПРАВОМУ НИЖНЬОМУ блоці натисніть «Створити кампанію без підказок» — це дасть вам повний контроль над усіма налаштуваннями.",
                tip: "Якщо обрати ціль (наприклад «Ліди»), Google приховає частину налаштувань — буде складніше підлаштовувати кампанію під ваш бізнес. «Без підказок» = гнучкість.",
            };

        // ── 3. Тип кампанії ──────────────────────────────────────────────
        case 3:
            return {
                title: "Тип кампанії",
                intro: "Виберіть «Пошук». Цей тип показує текстові оголошення в результатах пошуку Google, коли користувачі вводять відповідні ключові слова.",
                fields: [
                    { label: "Тип", value: "Пошук (Search)" },
                ],
                tip: "Performance Max і Display чудово працюють для брендів з великим бюджетом і чіткою воронкою. Search — найкращий старт для малого бізнесу: ви платите лише за тих, хто АКТИВНО шукає вашу послугу.",
            };

        // ── 4. Результати, цілі, назва ──────────────────────────────────
        case 4: {
            const goalLabel = ({
                phone_call: "Телефонні дзвінки",
                form_submit: "Надсилання форм через веб-сайт",
                page_visit: "Відвідування сторінки",
                call: "Телефонні дзвінки",
                form: "Надсилання форм через веб-сайт",
                visit: "Відвідування сторінки",
            })[targetAction] || "Надсилання форм через веб-сайт";

            return {
                title: "Результати, цілі та назва кампанії",
                intro: "На екрані «Виберіть результати»:\n• Поставте галочку «Відвідування веб-сайту» і вставте URL сайту\n• Якщо приймаєте дзвінки — поставте «Телефонні дзвінки» і додайте номер\n\nНижче у блоці «Виберіть потрібні цілі» оберіть основну ціль конверсії зі списку. Внизу екрана — поле для назви кампанії.\n\nГалочку «Увімкнути розширене відстеження конверсій» залиште увімкненою — це покращує точність даних.",
                fields: [
                    { label: "URL сайту (Відвідування веб-сайту)", value: siteUrl || "—" },
                    { label: "Телефон (Телефонні дзвінки)", value: businessPhone || "—", hint: !businessPhone ? "Опціонально — додайте у Налаштуваннях якщо приймаєте дзвінки" : null },
                    { label: "Основна ціль", value: goalLabel, noCopy: true },
                    { label: "Назва кампанії", value: campaign?.name || "—" },
                ],
                tip: "Назва кампанії бачите тільки ви — клієнти її не побачать. Корисно включити в назву місто і тип послуги: легше відрізняти, якщо в майбутньому буде кілька кампаній.",
            };
        }

        // ── 5. Призначення ставок ───────────────────────────────────────
        case 5: {
            const baseTip = "На старті, поки немає даних про конверсії, найкраще працюють Maximize Clicks або Manual CPC. Коли збереться ≥30 конверсій (зазвичай 2-4 тижні) — можна переключитись на Target CPA, і Google буде сам шукати найкращі кліки.";
            const tipText = campaign?.bidding_note
                ? `Чому саме ця стратегія: ${campaign.bidding_note}\n\n${baseTip}`
                : baseTip;
            return {
                title: "Призначення ставок",
                intro: "Оберіть стратегію автоматичного або ручного призначення ставок. Це визначає, скільки Google буде платити за кожен клік або конверсію.",
                fields: [
                    { label: "Стратегія ставок", value: prettyBidding(campaign?.bidding_strategy), noCopy: true },
                    budget?.estimated_avg_cpc != null ? { label: "Очікуваний середній CPC", value: `$${budget.estimated_avg_cpc}`, noCopy: true } : null,
                ].filter(Boolean),
                tip: tipText,
            };
        }

        // ── 6. Налаштування кампанії ────────────────────────────────────
        case 6:
            return {
                title: "Налаштування кампанії (мережі, гео, мова)",
                intro: "На цьому екрані налаштовуються: де показувати рекламу (мережі), кому (місцеположення + мова), і опціонально — аудиторії.",
                fields: [
                    { label: "Мережі", value: "Тільки «Пошукова мережа Google»\n☒ ВИМКНУТИ «Пошуковий партнер Google»\n☒ ВИМКНУТИ «Контекстно-медійна мережа»", multiline: true, noCopy: true },
                    { label: "Місцеположення", value: campaign?.geo || "—", noCopy: true, hint: "У підменю «Параметри місцеположення» оберіть «Присутність: люди в моїх цільових регіонах» (не Interest!)" },
                    { label: "Мова", value: prettyLang(campaign?.language_target), noCopy: true, hint: "Це мова інтерфейсу Google, якою користуються ваші потенційні клієнти" },
                    { label: "Аудиторії", value: "Не обирайте на старті", noCopy: true, hint: "Аудиторії звужують охоплення. Краще додати їх через 2-3 тижні в режимі «Спостереження», коли збереться статистика." },
                ],
                warning: "Не залишайте «Контекстно-медійна мережа» і «Пошукові партнери» увімкненими — це зливає бюджет на нерелевантний трафік. Увімкнути партнерів можна пізніше, коли кампанія стабілізується.",
                tip: "«Присутність» = люди ФАКТИЧНО у вашому місті. «Interest» = ще й ті, хто гуглить про ваше місто з інших регіонів (туристи, ті хто переїжджає) — даремні витрати.",
            };

        // ── 7. AI Max ───────────────────────────────────────────────────
        case 7: {
            // Recommend based on user experience proxy (price tier / strategy maturity).
            // Default: РЕКОМЕНДУЄМО ВИМКНУТИ для початківців.
            return {
                title: "AI Max (оптимізація об'єктів і заміна URL)",
                intro: "Google запропонує увімкнути «AI Max» — це означає, що Google буде:\n• автоматично генерувати додаткові заголовки/описи через AI\n• підставляти найкращу landing page з вашого сайту (замість зазначеної)\n\nЦе зручно, але втрачається контроль над тим, що бачить клієнт.",
                fields: [
                    { label: "Рекомендація", value: "ВИМКНУТИ AI Max на старті", hint: "Залиште тексти, які ви скопіюєте з нашої стратегії. Через 1-2 місяці, коли побачите які заголовки працюють — можете спробувати увімкнути." },
                ],
                tip: "AI Max добре працює для досвідчених рекламодавців з налагодженим відстеженням конверсій і великою кількістю даних. Для нового акаунта — ризик: AI згенерує тексти, які ви не контролюєте, і складно зрозуміти що працює, а що ні.",
            };
        }

        // ── 8. Перша група оголошень + розширення ──────────────────────
        case 8: {
            // Читаємо через хелпери adAssets — розуміють і legacy-об'єкти,
            // і рядки/масиви з multi-stage mapper. Без цього тут були
            // порожні ключові слова і «Заголовки RSA (0 шт)».
            const kwArr = getGroupKeywords(group);
            const kwList = kwArr.join("\n");
            const negArr = getGroupNegatives(group);
            const negs = negArr.join("\n");
            const headlines = collectGroupHeadlines(group);
            const descriptions = collectGroupDescriptions(group);
            const hasMoreGroups = totalGroups > 1;

            const callouts = ext.callouts || [];
            const sitelinks = ext.sitelinks || [];
            const callRecommended = ext.call_extension_recommended;

            return {
                title: `Перша група оголошень — «${group?.name || `Група ${groupIdx + 1}`}»`,
                intro: `На цьому кроці майстра Google Ads створюється лише ОДНА група оголошень + розширення (Assets). Введіть назву, додайте ключові слова з типами відповідності, створіть Responsive Search Ad і додайте розширення.\n\n${hasMoreGroups ? `У вашій стратегії є ще ${totalGroups - 1} ${totalGroups - 1 === 1 ? "група" : "груп"} — ми додамо їх ПІСЛЯ публікації кампанії на кроках ${11}–${totalSteps}.\n\n` : ""}Тема цієї групи: ${group?.theme || "—"}.`,
                fields: [
                    {
                        label: "Назва групи оголошень",
                        value: group?.name || "—",
                        hint: "⚠ При створенні кампанії ви НЕ зможете ввести назву першої групи оголошень — Google Ads не показує це поле в майстрі. Назву можна буде задати пізніше, коли завершите створення кампанії (на фінальному кроці буде поле для цього).",
                    },
                    { label: `Ключові слова (${kwArr.length} шт)`, value: kwList || "—", multiline: true, hint: "[слово] = точна відповідність · \"слово\" = фраза · слово = широка" },
                    negs ? { label: `Мінус-слова цієї групи (${negArr.length} шт)`, value: negs, multiline: true } : null,
                    { label: `Заголовки RSA (${headlines.length} шт)`, value: headlines.join("\n") || "—", multiline: true, hint: "Вставте по одному в поля Headline 1, 2, 3... Чим більше унікальних — тим краще міксує Google." },
                    { label: `Описи RSA (${descriptions.length} шт)`, value: descriptions.join("\n") || "—", multiline: true, hint: "Поля Description 1, 2, 3, 4. Pin лише ОДИН опис, якщо критично — решту залиште без піна." },
                    { label: "Кінцевий URL (Final URL)", value: siteUrl || "—" },

                    // ─── Розширення (Assets) — раніше були окремим кроком 9 ─────
                    { label: "── Розширення оголошень (Assets) ──", value: "Після створення RSA в тому ж екрані Google запропонує додати «Об'єкти» (Assets). Це безкоштовно і підвищує CTR на 10-20%.", multiline: true, noCopy: true },
                    { label: `Додаткова інформація / Callouts (${callouts.length} шт)`, value: callouts.join("\n") || "—", multiline: true, hint: "Короткі переваги (до 25 символів кожна). Мінімум 4." },
                    {
                        label: `Посилання сайту / Sitelinks (${sitelinks.length} шт)`,
                        value: sitelinks.length ? sitelinks.map(s => `${s.text || ""}${s.url ? "  →  " + s.url : ""}`).join("\n") : "—",
                        multiline: true,
                        hint: "Sitelinks — найпотужніший інструмент. Кожен sitelink — це додатковий рядок під вашим оголошенням, на який можна клікнути. Дає клієнту змогу одразу перейти до потрібного розділу. Текст + URL. Мінімум 4 — наприклад: Ціни, Відгуки, Контакти, Послуги.",
                    },
                    { label: "Дзвінки (Call extension)", value: callRecommended ? (businessPhone || "Увімкнути та додати ваш номер") : "Не критично для цієї стратегії", hint: callRecommended ? "Поле «Тип об'єкта» → «Дзвінки»" : null },
                    { label: "Ціни, акції, структуровані описи", value: "Опційно", hint: "Додайте, якщо є чим — наприклад, прайс-лист або 'Знижка 10% першим клієнтам'" },
                ].filter(Boolean),
                tip: "Додаткові групи створюються в розділі «Групи оголошень» вже після того, як кампанія опублікована — Google Ads не дозволяє створити кілька груп під час майстра.",
            };
        }

        // ── 9. Бюджет ──────────────────────────────────────────────────
        case 9:
            return {
                title: "Денний бюджет",
                intro: "Google запропонує 3 варіанти (зазвичай Low / Recommended / High) + кнопку «Встановити власний». Натисніть «Встановити власний» і введіть денний бюджет з нашої стратегії.",
                fields: [
                    { label: "Денний бюджет", value: dailyBudget != null ? `$${dailyBudget}` : "—" },
                    { label: "Очікувані витрати/міс", value: budget?.monthly_usd != null ? `$${budget.monthly_usd}` : "—" },
                ],
                tip: "Бюджет можна змінювати в будь-який момент після запуску — без перезапуску. Google може тимчасово витрачати до 2x від денного бюджету в певні дні (особливо вихідні), але середнє за місяць буде в межах.",
            };

        // ── 10. Перегляд і публікація ──────────────────────────────────
        case 10: {
            const allNegs = Array.from(new Set(
                ((strategy?.campaigns || [campaign]) || []).flatMap(c => (c.ad_groups || []).flatMap(g => g.negative_keywords || [])).map(s => String(s).replace(/^-/, ""))
            ));
            const hasMoreGroups = totalGroups > 1;
            return {
                title: hasMoreGroups ? "Перегляд, публікація, назва групи та мінус-слова" : "Перегляд, публікація та назва групи 🚀",
                intro: hasMoreGroups
                    ? `Фінальна сторінка «Кампанію можна опублікувати». Пробіжіться по налаштуваннях, натисніть «Опублікувати». Після публікації — задайте назву першої групи оголошень і додайте мінус-слова (інструкції нижче).\n\nКампанія буде запущена з ПЕРШОЮ групою оголошень. Далі ми повернемось сюди, щоб додати решту ${totalGroups - 1} ${totalGroups - 1 === 1 ? "групи" : "груп"}.`
                    : "Фінальна сторінка «Кампанію можна опублікувати». Google покаже зведення налаштувань — пробіжіться по ньому, переконайтеся що все правильно, і натискайте «Опублікувати». Після публікації — задайте назву першої групи оголошень і додайте мінус-слова (інструкції нижче).",
                fields: [
                    {
                        label: "Як перейменувати першу групу оголошень (інструкція)",
                        value: "Після публікації кампанії:\n1.  Зліва відкрийте «Кампанії» → ваша кампанія\n2.  Перейдіть на вкладку «Групи оголошень»\n3.  Знайдіть автоматично створену групу (зазвичай «Група оголошень 1»)\n4.  Наведіть курсор на її назву → натисніть олівець ✎ / клікніть на назву\n5.  Введіть рекомендовану назву нижче і збережіть",
                        multiline: true,
                        noCopy: true,
                    },
                    {
                        label: "Рекомендована назва першої групи оголошень",
                        value: group?.name || "Група 1",
                        editable: true,
                        editTarget: "firstGroupName",
                        hint: "Можете залишити запропоновану нами назву або відредагувати під свій смак. Це значення буде використане в підказках на наступних кроках.",
                    },
                    ...(allNegs.length > 0 ? [
                        {
                            label: "Що таке мінус-слова і навіщо вони",
                            value: "Мінус-слова — це слова, при яких Google НЕ показуватиме вашу рекламу. Без них кампанія «зливає» бюджет на нерелевантні запити: «безкоштовно», «своїми руками», «робота», назви інших міст тощо. Додати мінус-слова = одразу зекономити 20-40% бюджету.",
                            multiline: true,
                            noCopy: true,
                        },
                        {
                            label: "Як додати мінус-слова (інструкція)",
                            value: "1.  Зліва відкрийте «Кампанії»\n2.  Натисніть на вашу кампанію\n3.  Перейдіть у «Групи оголошень»\n4.  Виберіть групу оголошень\n5.  Відкрийте вкладку «Мінус-слова»\n6.  Натисніть «+»\n7.  Вставте список мінус-слів нижче\n8.  Збережіть\n\nПовторіть для кожної групи (або: створіть «Список виключених ключових слів» один раз і застосуйте до всіх кампаній — це швидше).",
                            multiline: true,
                            noCopy: true,
                        },
                        {
                            label: `Мінус-слова (${allNegs.length} шт) — вставте у поле «+ Додати»`,
                            value: allNegs.join("\n"),
                            multiline: true,
                        },
                    ] : []),
                ],
                doneLabel: hasMoreGroups ? "Я опублікував кампанію →" : "Я запустив рекламу 🎉",
                tip: "Після публікації статус має стати «Підлягає показу» (Eligible) — це значить, що кампанія активна і Google почне показувати оголошення. Якщо «На розгляді» (In review) — зачекайте до 24 годин на модерацію.\n\nПерші результати з'являться через 3-7 днів. Не панікуйте, якщо в перший день мало кліків — Google вчиться вашій аудиторії.",
            };
        }

        default:
            return null;
    }
}

/* ─── Builder for additional ad-group steps (12+, after campaign published) ─ */
function buildAdditionalGroupStep({ step, totalSteps, group, groupIdx, totalGroups, siteUrl }) {
    // Читаємо через хелпери adAssets — розуміють і legacy-об'єкти,
    // і рядки/масиви з multi-stage mapper. Без цього тут були
    // порожні ключові слова і «Заголовки RSA (0 шт)».
    const kwArr = getGroupKeywords(group);
    const kwList = kwArr.join("\n");
    const negArr = getGroupNegatives(group);
    const negs = negArr.join("\n");
    const headlines = collectGroupHeadlines(group);
    const descriptions = collectGroupDescriptions(group);
    const isLast = step === totalSteps;
    const stepIntro = step === 11
        ? "Кампанія опублікована 🎉 Тепер додамо решту груп оголошень.\n\nУ Google Ads відкрийте вашу кампанію → перейдіть на вкладку «Групи оголошень» → натисніть «+ Нова група оголошень». Тип: Standard."
        : "Так само як і попередня — у вкладці «Групи оголошень» натисніть «+ Нова група оголошень».";

    return {
        title: `Додати групу оголошень — «${group?.name || `Група ${groupIdx + 1}`}»`,
        intro: `${stepIntro}\n\nТема цієї групи: ${group?.theme || "—"}.`,
        fields: [
            { label: "Назва групи оголошень", value: group?.name || "—" },
            { label: `Ключові слова (${kwArr.length} шт)`, value: kwList || "—", multiline: true, hint: "[слово] = точна відповідність · \"слово\" = фраза · слово = широка" },
            negs ? { label: `Мінус-слова цієї групи (${negArr.length} шт)`, value: negs, multiline: true } : null,
            { label: `Заголовки RSA (${headlines.length} шт)`, value: headlines.join("\n") || "—", multiline: true },
            { label: `Описи RSA (${descriptions.length} шт)`, value: descriptions.join("\n") || "—", multiline: true },
            { label: "Кінцевий URL (Final URL)", value: siteUrl || "—" },
        ].filter(Boolean),
        doneLabel: isLast ? "Я запустив рекламу 🎉" : "Готово — додати наступну групу →",
        tip: isLast ? "Це остання група. Після завершення ми переходимо на фінальний чекліст і ваша кампанія повноцінно активна." : null,
    };
}

/* ─── pretty-printers ─── */
function prettyBidding(b) {
    if (!b) return "—";
    const map = {
        manual_cpc: "Manual CPC (Ручний CPC)",
        maximize_clicks: "Maximize clicks (Максимум кліків)",
        target_cpa: "Target CPA (Цільова ціна за конверсію)",
        target_roas: "Target ROAS (Цільова рентабельність)",
        maximize_conversions: "Maximize conversions (Максимум конверсій)",
    };
    return map[b] || b;
}
function prettyLang(l) {
    if (!l) return "—";
    const map = { uk: "Українська", pl: "Польська", en: "Англійська", cs: "Чеська", ru: "Російська" };
    return map[l] || l;
}

/* ─── Helper for parent: derive checklist auto-ticks ──────────────────── */
export function computeChecklistFromWizard(state, strategy) {
    if (!state || !state.completed) return {};
    const campaigns = strategy?.campaigns || (strategy?.campaign ? [strategy.campaign] : []);
    const campIdx = state.currentCampaignIdx || 0;
    const campaign = campaigns[campIdx];
    const totalGroups = (campaign?.ad_groups || []).length || 1;

    const sk = (s) => `c${campIdx}-s${s}`;
    const grpSk = (s, g) => `c${campIdx}-s${s}-g${g}`;

    const done = state.completed;

    // Step 8 covers group 0 (with extensions); steps 11, 12, ... cover groups 1, 2, ...
    // For "all groups done" we require: step 8 + every post-publish group step.
    const allGroupsDone = () => {
        if (!done[grpSk(8, 0)]) return false;
        for (let g = 1; g < totalGroups; g++) {
            if (!done[grpSk(10 + g, g)]) return false;
        }
        return true;
    };

    // Soft tick — at least the FIRST group was processed (covers "min 1 variant per group" wording).
    const firstGroupDone = !!done[grpSk(8, 0)];

    return {
        login: !!(done[sk(1)] || done[sk(2)] || done[sk(3)]),
        campaign: !!(done[sk(3)] && done[sk(4)]),
        budget: !!done[sk(9)],
        adgroups: allGroupsDone(),
        keywords: firstGroupDone,            // soft: keywords were copied for first group
        negatives: !!done[sk(10)],
        ads: firstGroupDone,                 // soft: "min 1 variant per group" — covered by first group
        extensions: firstGroupDone,          // extensions are now part of step 8
        active: !!done[sk(10)],
    };
}